import React, { useContext, useEffect, useState } from "react";
import { useMutation } from "react-query";
import GlobalContext from "src/context/global-context";
import client from "../../../api";
import { useIsMounted } from "src/lib/hooks/use-is-mounted";
import {
  APISomethingWrongMsg,
  LIST_PAGE_SIZE,
  NetworkDisconnectMsg,
  permissions as permissionConstants,
} from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import PermissionGate from "src/components/shared/permission-gate";
import { getErrorMessage } from "src/lib/utils";
import {
  Table,
  Modal,
  Tag,
  Button,
  Tabs,
  Card,
  Typography,
  Image,
  Space,
  Form,
  Upload,
  Row,
  Col,
  Input,
  Select,
  Switch,
  Popconfirm,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { EyeIcon } from "src/components/icons/eye-icon";
import { EditIcon } from "src/components/icons/edit-icon";
import { DeleteIcon } from "src/components/icons/delete-icon";
const { Title, Text } = Typography;

const BlogPostsManagement = () => {
  const { setAlertText, setIsAlertOpened } = useContext(GlobalContext);
  const isMounted = useIsMounted();

  const { canView, canList, canAdd, canEdit, canDelete } = usePermissions(
    permissionConstants.LANDING_PAGE_MANAGEMENT_BLOG_POSTS
  );
  const canViewBlogs = canView || canList;

  const [tableData, setTableData] = useState<any[]>([]);
  const [selectedBlog, setSelectedBlog] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: LIST_PAGE_SIZE,
    total: 0,
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingBlogId, setEditingBlogId] = useState<number | null>(null);
  const [form] = Form.useForm();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [existingImagePath, setExistingImagePath] = useState("");
  const [uploadedImageUrl, setUploadedImageUrl] = useState("");
  const { mutate: getAllBlogsList, isLoading } = useMutation(
    client.landingPageApis.GetAllBlogsList,

    {
      onSuccess: (res: any) => {
        const items = res?.data?.items || [];
        setTableData(items);
        setPagination((prev) => ({
          ...prev,

          total: res?.data?.total || 0,
        }));
      },

      onError: (error: any) => {
        if (error?.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else if (error?.response) {
          setAlertText(getErrorMessage(error.response.data));
        } else {
          setAlertText(APISomethingWrongMsg);
        }
        setIsAlertOpened(true);
      },
    },
  );

  const { mutateAsync: uploadImage, isLoading: isUploadingImage } = useMutation(
    client.landingPageApis.UploadImage,
  );

  const { mutate: createBlog, isLoading: isLoadingCreateBlog } = useMutation(
    client.landingPageApis.CreateNewBlog,
    {
      onSuccess: () => {
        setSelectedFile(null);
        setExistingImagePath("");
        form.resetFields();
        setShowCreateModal(false);
        getAllBlogsList({
          page: pagination.page,
          limit: pagination.limit,
        });
      },
      onError: (error: any) => {
        if (error?.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else if (error?.response) {
          setAlertText(getErrorMessage(error.response.data));
        } else {
          setAlertText(APISomethingWrongMsg);
        }
        setIsAlertOpened(true);
      },
    },
  );
  const { mutate: updateBlog, isLoading: isLoadingUpdateBlog } = useMutation(
    client.landingPageApis.UpdateBlog,
    {
      onSuccess: () => {
        form.resetFields();
        setExistingImagePath("");
        setSelectedFile(null);
        setUploadedImageUrl("");
        setUploadedImageUrl("");
        setShowCreateModal(false);
        setEditingBlogId(null);
        setIsEditMode(false);
        getAllBlogsList({
          page: pagination.page,
          limit: pagination.limit,
        });
      },
      onError: (error: any) => {
        if (error?.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else if (error?.response) {
          setAlertText(getErrorMessage(error.response.data));
        } else {
          setAlertText(APISomethingWrongMsg);
        }
        setIsAlertOpened(true);
      },
    },
  );

  const { mutate: deleteBlog } = useMutation(
    client.landingPageApis.DeleteBlog,
    {
      onSuccess: () => {
        getAllBlogsList({
          page: pagination.page,
          limit: pagination.limit,
        });
      },
    },
  );

  const onDelete = (id: number) => {
    if (!canDelete) {
      setAlertText("You do not have permission to delete blogs");
      setIsAlertOpened(true);
      return;
    }
    deleteBlog(id);
  };

  const handleEditBlog = (blog: any) => {
    if (!canEdit) {
      setAlertText("You do not have permission to edit blogs");
      setIsAlertOpened(true);
      return;
    }
    setExistingImagePath(blog.image);
    setUploadedImageUrl(process.env.REACT_APP_S3_URL + blog.image);
    setSelectedFile(null);
    setIsEditMode(true);
    setEditingBlogId(blog.id);
    form.setFieldsValue({
      title_ge: blog.title?.[0],
      title_ru: blog.title?.[1],
      title_en: blog.title?.[2],
      short_ge: blog.short_description?.[0],
      short_ru: blog.short_description?.[1],
      short_en: blog.short_description?.[2],
      description_ge: blog.description?.[0],
      description_ru: blog.description?.[1],
      description_en: blog.description?.[2],
      tags: blog.tags,
      active: blog.active,
      is_featured: blog.is_featured,
    });

    setShowCreateModal(true);
  };

  const handleSubmitBlog = async (values: any) => {
    let imageUrl = existingImagePath;
    if (selectedFile) {
      const uploadFd = new FormData();
      uploadFd.append("file", selectedFile);
      const uploadRes: any = await uploadImage(uploadFd);
      // console.log("uploadRes", uploadRes);
      imageUrl = uploadRes?.data?.filePath;
    }

    const payload = {
      title: [values.title_ge, values.title_ru, values.title_en],
      short_description: [values.short_ge, values.short_ru, values.short_en],
      description: [
        values.description_ge,
        values.description_ru,
        values.description_en,
      ],
      tags: values.tags || [],
      author_name: "Admin",
      is_featured: values.is_featured,
      active: values.active,
      image: imageUrl,
    };

    if (isEditMode) {
      if (!canEdit) {
        setAlertText("You do not have permission to edit blogs");
        setIsAlertOpened(true);
        return;
      }
      updateBlog({
        id: editingBlogId,
        ...payload,
      });

      return;
    }

    if (!canAdd) {
      setAlertText("You do not have permission to add blogs");
      setIsAlertOpened(true);
      return;
    }
    createBlog(payload);
  };

  useEffect(() => {
    if (!isMounted || !canViewBlogs) return;

    getAllBlogsList({
      page: pagination.page,
      limit: pagination.limit,
    });
  }, [isMounted, canViewBlogs, pagination.page]);

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      width: 80,
    },

    {
      title: "Image",
      render: (_: any, record: any) => (
        <Image
          width={60}
          height={60}
          src={process.env.REACT_APP_S3_URL + record.image}
          style={{
            objectFit: "cover",
            borderRadius: 8,
          }}
        />
      ),
    },
    {
      title: "Title",
      render: (_: any, record: any) => record.title?.[2],
    },
    {
      title: "Description",
      render: (_: any, record: any) => record.short_description?.[2],
    },
    {
      title: "Author",
      dataIndex: "author_name",
    },
    {
      title: "Featured",
      render: (_: any, record: any) => (
        <Tag color={record.is_featured ? "gold" : "default"}>
          {record.is_featured ? "Featured" : "Normal"}
        </Tag>
      ),
    },
    {
      title: "Status",
      render: (_: any, record: any) => (
        <Tag color={record.active ? "green" : "red"}>
          {record.active ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      width: 180,
      render: (_: any, record: any) => {
        if (!canView && !canEdit && !canDelete) {
          return "—";
        }
        return (
          <Space>
            {canView && (
              <button
                onClick={() => {
                  setSelectedBlog(record);
                  setShowModal(true);
                }}
              >
                <EyeIcon className="w-5 h-5" fill="#6b7280" />
              </button>
            )}
            {canEdit && (
              <button onClick={() => handleEditBlog(record)}>
                <EditIcon className="w-5 h-5" fill="#2563eb" />
              </button>
            )}
            {canDelete && (
              <Popconfirm
                title="Delete Blog"
                description="Are you sure you want to delete this Blog?"
                onConfirm={() => onDelete(record.id)}
                okText="Yes"
                cancelText="No"
                okButtonProps={{
                  style: {
                    backgroundColor: "#ef4444",
                    borderColor: "#ef4444",
                    color: "#fff",
                  },
                }}
              >
                <button
                  type="button"
                  className="
                flex items-center justify-center
                rounded-lg
                p-2
                border
                border-red-200
                bg-white
                hover:bg-red-50
              "
                >
                  <DeleteIcon className="w-5 h-5" fill="#ef4444" stroke="#ef4444" />
                </button>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div className="p-6">
      <div className="mb-8 flex items-center justify-between">
        <h2 className="text-2xl font-bold">Blog Posts</h2>
        <PermissionGate
          module={[permissionConstants.LANDING_PAGE_MANAGEMENT_BLOG_POSTS]}
          action="can_add"
        >
          <Button
            type="primary"
            style={{
              background: "#1677ff",
              borderColor: "#1677ff",
            }}
            onClick={() => {
              setSelectedFile(null);
              setUploadedImageUrl("");
              setExistingImagePath("");
              setIsEditMode(false);
              setEditingBlogId(null);
              form.resetFields();
              setShowCreateModal(true);
            }}
          >
            + Add Blog
          </Button>
        </PermissionGate>
      </div>
      {!canViewBlogs ? (
        <div className="flex flex-col items-center justify-center bg-white rounded-xl p-8 text-center shadow-sm">
          <p className="font-manrope text-[16px] font-semibold text-secondary">
            You do not have permission to view blog posts.
          </p>
        </div>
      ) : (
        <Table
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={tableData}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,

            onChange: (page) => {
              setPagination((prev) => ({
                ...prev,
                page,
              }));
            },
          }}
        />
      )}

      {/* VIEW MODAL */}

      <Modal
        open={showModal}
        footer={null}
        width={900}
        title="Blog Details"
        onCancel={() => {
          setSelectedFile(null);
          setUploadedImageUrl("");
          setExistingImagePath("");
          setShowModal(false);
          setIsEditMode(false);
          setEditingBlogId(null);
          form.resetFields();
        }}
      >
        {selectedBlog && (
          <>
            <Image
              src={process.env.REACT_APP_S3_URL + selectedBlog.image}
              width="100%"
              style={{
                borderRadius: 12,
                marginBottom: 20,
              }}
            />

            <div className="mb-4">
              {/* <Tag color="blue">{selectedBlog.author_name}</Tag> */}

              <Tag color={selectedBlog.is_featured ? "gold" : "default"}>
                {selectedBlog.is_featured ? "Featured" : "Not Featured"}
              </Tag>
            </div>

            <div className="mb-4">
              <Text strong>Tags:</Text>

              <div className="mt-2">
                {selectedBlog.tags?.map((tag: string) => (
                  <Tag key={tag}>{tag}</Tag>
                ))}
              </div>
            </div>

            <Tabs
              items={[
                {
                  key: "1",
                  label: "🇬🇪 Georgian",
                  children: (
                    <Card>
                      <Title level={5}>Title</Title>
                      <Text>{selectedBlog.title?.[0]}</Text>
                      <br />
                      <br />
                      <Title level={5}>Short Description</Title>
                      <Text>{selectedBlog.short_description?.[0]}</Text>
                      <br />
                      <br />
                      <Title level={5}>Description</Title>
                      <Text>{selectedBlog.description?.[0]}</Text>
                    </Card>
                  ),
                },

                {
                  key: "2",
                  label: "🇷🇺 Russian",
                  children: (
                    <Card>
                      <Title level={5}>Title</Title>
                      <Text>{selectedBlog.title?.[1]}</Text>
                      <br />
                      <br />
                      <Title level={5}>Short Description</Title>
                      <Text>{selectedBlog.short_description?.[1]}</Text>
                      <br />
                      <br />
                      <Title level={5}>Description</Title>
                      <Text>{selectedBlog.description?.[1]}</Text>
                    </Card>
                  ),
                },

                {
                  key: "3",
                  label: "🇬🇧 English",
                  children: (
                    <Card>
                      <Title level={5}>Title</Title>
                      <Text>{selectedBlog.title?.[2]}</Text>
                      <br />
                      <br />
                      <Title level={5}>Short Description</Title>
                      <Text>{selectedBlog.short_description?.[2]}</Text>
                      <br />
                      <br />
                      <Title level={5}>Description</Title>
                      <Text>{selectedBlog.description?.[2]}</Text>
                    </Card>
                  ),
                },
              ]}
            />
          </>
        )}
      </Modal>

      {/* Add/update MODAL */}

      <Modal
        open={showCreateModal}
        width={1000}
        title={isEditMode ? "Edit Blog" : "Add Blog"}
        okText={isEditMode ? "Update Blog" : "Create Blog"}
        confirmLoading={isEditMode ? isLoadingUpdateBlog : isLoadingCreateBlog}
        okButtonProps={{
          style: {
            background: "#2563eb",
            borderColor: "#2563eb",
            color: "#fff",
          },
        }}
        onOk={() => {
          form.submit();
        }}
        onCancel={() => {
          setShowCreateModal(false);
          setSelectedFile(null);
          setUploadedImageUrl("");
          setExistingImagePath("");
          setIsEditMode(false);
          setEditingBlogId(null);
          form.resetFields();
        }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmitBlog}
          initialValues={{
            active: true,

            is_featured: false,
          }}
        >
          <Form.Item
            label="Image"
            required
            validateStatus={!selectedFile && !isEditMode ? "error" : ""}
            help={!selectedFile && !isEditMode ? "Image is required" : ""}
          >
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];

                if (file) {
                  setSelectedFile(file);
                }
              }}
            />
            {(selectedFile || uploadedImageUrl) && (
              <Image
                width={120}
                src={
                  selectedFile
                    ? URL.createObjectURL(selectedFile)
                    : uploadedImageUrl
                }
              />
            )}
          </Form.Item>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Title (GE)"
                name="title_ge"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label="Title (RU)"
                name="title_ru"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label="Title (EN)"
                name="title_en"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Short Description (GE)"
                name="short_ge"
                rules={[
                  {
                    required: true,
                    message: "Required",
                  },
                ]}
              >
                <Input.TextArea rows={3} />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label="Short Description (RU)"
                name="short_ru"
                rules={[
                  {
                    required: true,
                    message: "Required",
                  },
                ]}
              >
                <Input.TextArea rows={3} />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label="Short Description (EN)"
                name="short_en"
                rules={[
                  {
                    required: true,
                    message: "Required",
                  },
                ]}
              >
                <Input.TextArea rows={3} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Description (GE)"
                name="description_ge"
                rules={[
                  {
                    required: true,
                    message: "Required",
                  },
                ]}
              >
                <Input.TextArea rows={5} />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label="Description (RU)"
                name="description_ru"
                rules={[
                  {
                    required: true,
                    message: "Required",
                  },
                ]}
              >
                <Input.TextArea rows={5} />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label="Description (EN)"
                name="description_en"
                rules={[
                  {
                    required: true,
                    message: "Required",
                  },
                ]}
              >
                <Input.TextArea rows={5} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Tags" name="tags">
            <Select mode="tags" placeholder="finance, cards..." />
          </Form.Item>

          <Row gutter={24}>
            <Col>
              <Form.Item shouldUpdate noStyle>
                {() => (
                  <Form.Item
                    label="Featured"
                    name="is_featured"
                    valuePropName="checked"
                  >
                    <Switch
                      checkedChildren="ON"
                      unCheckedChildren="OFF"
                      style={{
                        backgroundColor: form.getFieldValue("is_featured")
                          ? "#16a34a"
                          : "#9ca3af",
                      }}
                      onChange={(val) => {
                        form.setFieldValue("is_featured", val);
                      }}
                    />
                  </Form.Item>
                )}
              </Form.Item>
            </Col>

            <Col>
              <Form.Item shouldUpdate noStyle>
                {() => (
                  <Form.Item
                    label="Active"
                    name="active"
                    valuePropName="checked"
                  >
                    <Switch
                      checkedChildren="ON"
                      unCheckedChildren="OFF"
                      style={{
                        backgroundColor: form.getFieldValue("active")
                          ? "#16a34a"
                          : "#9ca3af",
                      }}
                      onChange={(val) => {
                        form.setFieldValue("active", val);
                      }}
                    />
                  </Form.Item>
                )}
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default BlogPostsManagement;
